import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/store';
import { fetchCategories } from '@/store/slices/productsSlice';
import { useProductFilters } from '@/contexts/ProductFilterContext';
import RoleSwitcherDropdown from '@/components/ui/RoleSwitcherDropdown';
import { PRODUCT_CONDITIONS } from '@/utils/constants';
import { useAuth } from '@/hooks/useAuth';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  ChartBarIcon,
  CogIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  HeartIcon,
  StarIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  CubeIcon,
  UserIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  PlusIcon,
  FunnelIcon,
  MapPinIcon,
  TagIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  roles: string[];
  children?: NavigationItem[];
}


const navigationItems: NavigationItem[] = [
  // Dashboard
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: HomeIcon,
    roles: ['super_admin', 'admin', 'staff', 'seller', 'buyer'],
  },
  
  // Super Admin & Admin - User Management
  {
    name: 'User Management',
    href: '/dashboard/super-admin/users',
    icon: UserGroupIcon,
    roles: ['super_admin', 'admin'],
    children: [
      {
        name: 'All Users',
        href: '/dashboard/super-admin/users',
        icon: UserGroupIcon,
        roles: ['super_admin', 'admin'],
      },
      {
        name: 'User Roles',
        href: '/dashboard/super-admin/user-roles',
        icon: ShieldCheckIcon,
        roles: ['super_admin'],
      },
    ],
  },
  
  // Super Admin & Admin - Shop Management
  {
    name: 'Shop Management',
    href: '/dashboard/super-admin/shops',
    icon: BuildingStorefrontIcon,
    roles: ['super_admin', 'admin'],
    children: [
      {
        name: 'All Shops',
        href: '/dashboard/super-admin/shops',
        icon: BuildingStorefrontIcon,
        roles: ['super_admin', 'admin'],
      },
      {
        name: 'Shop Approvals',
        href: '/dashboard/super-admin/shop-approvals',
        icon: ExclamationTriangleIcon,
        roles: ['super_admin', 'admin'],
      },
    ],
  },
  
  // Super Admin & Admin - Product Management
  {
    name: 'Product Management',
    href: '/dashboard/super-admin/products',
    icon: CubeIcon,
    roles: ['super_admin', 'admin'],
    children: [
      {
        name: 'All Products',
        href: '/dashboard/super-admin/products',
        icon: CubeIcon,
        roles: ['super_admin', 'admin'],
      },
      {
        name: 'Product Moderation',
        href: '/dashboard/super-admin/product-moderation',
        icon: ExclamationTriangleIcon,
        roles: ['super_admin', 'admin'],
      },
    ],
  },
  
  // Super Admin & Admin - Order Management
  {
    name: 'Order Management',
    href: '/dashboard/super-admin/orders',
    icon: ClipboardDocumentListIcon,
    roles: ['super_admin', 'admin'],
  },
  
  // Super Admin & Admin - Analytics
  {
    name: 'Analytics',
    href: '/dashboard/super-admin/analytics',
    icon: ChartBarIcon,
    roles: ['super_admin', 'admin'],
  },
  
  // Super Admin & Admin & Staff - Homepage Management
  {
    name: 'Homepage Management',
    href: '/dashboard/super-admin/homepage',
    icon: HomeIcon,
    roles: ['super_admin', 'admin', 'staff'],
  },
  
  // Super Admin & Admin - Location Management
  {
    name: 'Location Management',
    href: '/dashboard/super-admin/locations',
    icon: MapPinIcon,
    roles: ['super_admin', 'admin'],
  },
  
  // Super Admin - System Settings
  {
    name: 'System Settings',
    href: '/dashboard/super-admin/settings',
    icon: CogIcon,
    roles: ['super_admin'],
    children: [
      {
        name: 'Business Settings',
        href: '/dashboard/super-admin/business-settings',
        icon: CogIcon,
        roles: ['super_admin'],
      },
      {
        name: 'Commission Settings',
        href: '/dashboard/super-admin/commission-settings',
        icon: BanknotesIcon,
        roles: ['super_admin'],
      },
    ],
  },
  
  // Seller - Shop Management (available to users who can access seller functionality)
  {
    name: 'My Shop',
    href: '/dashboard/seller/shop',
    icon: BuildingStorefrontIcon,
    roles: ['seller', 'admin', 'super_admin'],
  },
  
  // Seller - Product Management (available to users who can access seller functionality)
  {
    name: 'Products',
    href: '/dashboard/seller/products',
    icon: CubeIcon,
    roles: ['seller', 'admin', 'super_admin'],
    children: [
      {
        name: 'My Products',
        href: '/dashboard/seller/products',
        icon: CubeIcon,
        roles: ['seller', 'admin', 'super_admin'],
      },
      {
        name: 'Add Product',
        href: '/dashboard/seller/products/add',
        icon: PlusIcon,
        roles: ['seller', 'admin', 'super_admin'],
      },
    ],
  },
  
  // Seller - Inventory (available to users who can access seller functionality)
  {
    name: 'Inventory',
    href: '/dashboard/seller/inventory',
    icon: ClipboardDocumentListIcon,
    roles: ['seller', 'admin', 'super_admin'],
  },
  
  // Seller - Orders (available to users who can access seller functionality)
  {
    name: 'Orders',
    href: '/dashboard/seller/orders',
    icon: TruckIcon,
    roles: ['seller', 'admin', 'super_admin'],
  },
  
  // Seller - Analytics (available to users who can access seller functionality)
  {
    name: 'Analytics',
    href: '/dashboard/seller/analytics',
    icon: ChartBarIcon,
    roles: ['seller', 'admin', 'super_admin'],
  },
  
  // Seller - Commission (available to users who can access seller functionality)
  {
    name: 'Commission',
    href: '/dashboard/seller/commission',
    icon: CurrencyDollarIcon,
    roles: ['seller', 'admin', 'super_admin'],
  },
  
  // Buyer - Products
  {
    name: 'Products',
    href: '/products',
    icon: ShoppingBagIcon,
    roles: ['buyer'],
  },
  
  // Buyer - Cart
  {
    name: 'Cart',
    href: '/dashboard/buyer/cart',
    icon: ShoppingCartIcon,
    roles: ['buyer'],
  },
  
  // Buyer - Orders
  {
    name: 'My Orders',
    href: '/dashboard/buyer/orders',
    icon: ClipboardDocumentListIcon,
    roles: ['buyer'],
  },
  
  // Buyer - Wishlist
  {
    name: 'Wishlist',
    href: '/dashboard/buyer/wishlist',
    icon: HeartIcon,
    roles: ['buyer'],
  },
  
  // Buyer - Reviews
  {
    name: 'My Reviews',
    href: '/dashboard/buyer/reviews',
    icon: StarIcon,
    roles: ['buyer'],
  },
  
  // Staff - Content Moderation
  {
    name: 'Content Moderation',
    href: '/dashboard/staff/moderation',
    icon: ExclamationTriangleIcon,
    roles: ['staff'],
    children: [
      {
        name: 'Pending Products',
        href: '/dashboard/staff/products',
        icon: CubeIcon,
        roles: ['staff'],
      },
      {
        name: 'Pending Reviews',
        href: '/dashboard/staff/reviews',
        icon: StarIcon,
        roles: ['staff'],
      },
    ],
  },
  
  // Staff - User Support
  {
    name: 'User Support',
    href: '/dashboard/staff/support',
    icon: UserGroupIcon,
    roles: ['staff'],
  },
];

const Sidebar: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { categories } = useSelector((state: RootState) => state.products);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [showProductFilters, setShowProductFilters] = useState(false);
  
  // Use context for filters
  const { filters, updateFilter, applyFilters } = useProductFilters();
  
  // Use useAuth hook to get the actual user role
  const { getUserRoleName, isSuperAdmin, isAdmin, isStaff, isUser } = useAuth();
  const actualUserRole = getUserRoleName(); // Get role from role_id mapping
  const isProductsPage = location.pathname === '/products';
  
  // Helper function to check if a role is allowed for the current user
  const isRoleAllowed = (role: string): boolean => {
    if (role === 'super_admin') return isSuperAdmin();
    if (role === 'admin') return isAdmin() || isSuperAdmin();
    if (role === 'staff') return isStaff() || isAdmin() || isSuperAdmin();
    if (role === 'seller') return isUser() || isAdmin() || isSuperAdmin();
    if (role === 'buyer') return isUser();
    return false;
  };
  
  // Get current role from URL query parameter, localStorage, or infer from path
  const urlParams = new URLSearchParams(location.search);
  let currentRole = urlParams.get('role');
  
  // If no role in query params, check localStorage
  if (!currentRole) {
    const savedRole = localStorage.getItem('selectedRole');
    if (savedRole && isRoleAllowed(savedRole)) {
      currentRole = savedRole;
    }
  }
  
  // If still no role, infer from URL path (but validate it)
  if (!currentRole) {
    if (location.pathname.startsWith('/dashboard/super-admin')) {
      currentRole = isSuperAdmin() ? 'super_admin' : null;
    } else if (location.pathname.startsWith('/dashboard/admin')) {
      currentRole = (isAdmin() || isSuperAdmin()) ? 'admin' : null;
    } else if (location.pathname.startsWith('/dashboard/staff')) {
      currentRole = (isStaff() || isAdmin() || isSuperAdmin()) ? 'staff' : null;
    } else if (location.pathname.startsWith('/dashboard/seller')) {
      currentRole = (isUser() || isAdmin() || isSuperAdmin()) ? 'seller' : null;
    } else if (location.pathname.startsWith('/dashboard/buyer')) {
      currentRole = isUser() ? 'buyer' : null;
    }
  }
  
  // If still no valid role, use the actual user role and set appropriate mode
  if (!currentRole || !isRoleAllowed(currentRole)) {
    if (isSuperAdmin()) {
      currentRole = 'super_admin';
    } else if (isAdmin()) {
      currentRole = 'admin';
    } else if (isStaff()) {
      currentRole = 'staff';
    } else if (isUser()) {
      // For regular users, default to buyer mode
      currentRole = 'buyer';
      // Save to localStorage
      localStorage.setItem('selectedRole', 'buyer');
    } else {
      currentRole = 'user';
    }
  }

  useEffect(() => {
    if (isProductsPage) {
      dispatch(fetchCategories() as any);
      setShowProductFilters(true);
    } else {
      setShowProductFilters(false);
    }
  }, [isProductsPage, dispatch]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    );
  };

  const isItemActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const hasPermission = (roles: string[], itemHref?: string) => {
    // Show items based on the current role selected in the URL
    // But validate that the user actually has permission to use this role
    if (!currentRole || !isRoleAllowed(currentRole)) {
      return false;
    }
    return roles.includes(currentRole);
  };

  const handleCategoryToggle = (categoryId: number) => {
    const newCategories = filters.categories.includes(categoryId)
      ? filters.categories.filter(id => id !== categoryId)
      : [...filters.categories, categoryId];
    updateFilter('categories', newCategories);
  };

  const handleLocationToggle = (location: string) => {
    const newLocations = filters.locations.includes(location)
      ? filters.locations.filter(loc => loc !== location)
      : [...filters.locations, location];
    updateFilter('locations', newLocations);
  };

  const handleBrandToggle = (brand: string) => {
    const newBrands = filters.brands.includes(brand)
      ? filters.brands.filter(b => b !== brand)
      : [...filters.brands, brand];
    updateFilter('brands', newBrands);
  };

  const handleConditionToggle = (condition: string) => {
    const newConditions = filters.conditions.includes(condition)
      ? filters.conditions.filter(c => c !== condition)
      : [...filters.conditions, condition];
    updateFilter('conditions', newConditions);
  };

  const handleProductTypeToggle = (productType: string) => {
    const newProductTypes = filters.productTypes.includes(productType)
      ? filters.productTypes.filter(pt => pt !== productType)
      : [...filters.productTypes, productType];
    updateFilter('productTypes', newProductTypes);
  };

  const clearAllFilters = () => {
    updateFilter('categories', []);
    updateFilter('locations', []);
    updateFilter('priceRange', { min: 0, max: 10000 });
    updateFilter('brands', []);
    updateFilter('conditions', []);
    updateFilter('productTypes', []);
  };

  const filteredNavigation = navigationItems.filter(item => hasPermission(item.roles, item.href));

  // Get role-specific dashboard URL based on current role
  const getDashboardUrl = () => {
    return `/dashboard?role=${currentRole}`;
  };

  // Helper to add role parameter to navigation links
  const addRoleToUrl = (href: string) => {
    if (!href || href.startsWith('http') || href.startsWith('/auth')) {
      return href;
    }
    
    // If href already has query params, append role; otherwise add it
    if (href.includes('?')) {
      return `${href}&role=${currentRole}`;
    } else {
      return `${href}?role=${currentRole}`;
    }
  };

  const renderNavigationItem = (item: NavigationItem, level: number = 0, index?: number) => {
    if (!hasPermission(item.roles, item.href)) return null;

    // Override dashboard href for role-specific routing
    let itemHref = item.name === 'Dashboard' ? getDashboardUrl() : item.href;
    
    // Add role parameter to dashboard routes to preserve role context
    if (itemHref.startsWith('/dashboard') && !itemHref.includes('role=')) {
      itemHref = addRoleToUrl(itemHref);
    }
    
    const isActive = isItemActive(itemHref);
    const isExpanded = expandedItems.includes(item.name);
    const hasChildren = item.children && item.children.length > 0;
    
    // Create unique key using name, href, and optional index
    const uniqueKey = index !== undefined 
      ? `${item.name}-${item.href}-${index}` 
      : `${item.name}-${item.href}`;

    return (
      <div key={uniqueKey}>
        <div
          className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md cursor-pointer transition-colors ${
            level > 0 ? 'ml-4' : ''
          } ${
            isActive
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.name);
            }
          }}
        >
          <Link
            to={itemHref}
            className="flex items-center flex-1"
            onClick={(e) => {
              if (hasChildren) {
                e.preventDefault();
              }
            }}
          >
            <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
            {item.name}
          </Link>
          {hasChildren && (
            <ChevronRightIcon
              className={`h-4 w-4 transition-transform ${
                isExpanded ? 'rotate-90' : ''
              }`}
            />
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.children?.map((child, index) => renderNavigationItem(child, level + 1, index))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-md bg-white shadow-md"
        >
          {isOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 relative flex items-center justify-center">
                  {/* Trash Can Logo with Sparkles - Matching Header */}
                  <svg viewBox="0 0 64 64" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <filter id="sidebar-glow">
                        <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Bin Body - Dark Teal Grey */}
                    <rect x="14" y="24" width="36" height="32" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5"/>
                    
                    {/* Vertical Cyan Stripes/Ridges with Glow */}
                    <rect x="18" y="26" width="3" height="28" rx="0.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.9"/>
                    <rect x="24" y="26" width="3" height="28" rx="0.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.9"/>
                    <rect x="30" y="26" width="3" height="28" rx="0.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.9"/>
                    <rect x="36" y="26" width="3" height="28" rx="0.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.9"/>
                    <rect x="42" y="26" width="3" height="28" rx="0.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.9"/>
                    
                    {/* Bin Lid - Dark Teal Grey, Slightly Ajar */}
                    <rect x="10" y="18" width="44" height="8" rx="2" fill="#2d4a52" stroke="#1e2d33" strokeWidth="1.5" transform="rotate(-8 32 22)"/>
                    
                    {/* Lid Opening - Cyan Interior */}
                    <ellipse cx="32" cy="22" rx="18" ry="3" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.7"/>
                    
                    {/* Sparkles - Four Pointed Stars */}
                    <g filter="url(#sidebar-glow)">
                      {/* Large Sparkle */}
                      <path d="M 40 8 L 41 11 L 44 10 L 41 12 L 42 15 L 40 13 L 38 15 L 39 12 L 36 10 L 39 11 Z" fill="#00d9ff" opacity="0.9"/>
                      {/* Small Sparkle */}
                      <path d="M 48 4 L 48.5 5.5 L 50 5 L 48.5 6 L 49 7.5 L 48 6.5 L 47 7.5 L 47.5 6 L 46 5 L 47.5 5.5 Z" fill="#00d9ff" opacity="0.9"/>
                    </g>
                    
                    {/* Dots */}
                    <circle cx="45" cy="6" r="1.5" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.8"/>
                    <circle cx="50" cy="8" r="1" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.8"/>
                    <circle cx="43" cy="10" r="1" fill="#00d9ff" filter="url(#sidebar-glow)" opacity="0.8"/>
                    
                    {/* Base/Shadow */}
                    <ellipse cx="32" cy="58" rx="20" ry="2" fill="#1e2d33" opacity="0.6"/>
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900">Recycler-Bin</h1>
                <p className="text-xs text-gray-500 capitalize">{(currentRole || actualUserRole).replace('_', ' ')}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Role Switcher - For all users with multiple role access */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <RoleSwitcherDropdown />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item, index) => renderNavigationItem(item, 0, index))}
            
            {/* Product Filters - Only show on products page */}
            {showProductFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center">
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    Filters
                  </h3>
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Clear All
                  </button>
                </div>

                {/* Categories */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('categories')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <TagIcon className="h-4 w-4 mr-2" />
                      Categories
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('categories') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('categories') && (
                    <div className="mt-2 space-y-1 pl-6">
                      {categories.map((category) => (
                        <label key={category.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.categories.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{category.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Locations */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('locations')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      Location
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('locations') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('locations') && (
                    <div className="mt-2 space-y-1 pl-6">
                      {['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia', 'Japan'].map((location) => (
                        <label key={location} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.locations.includes(location)}
                            onChange={() => handleLocationToggle(location)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{location}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Price Range */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('price')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                      Price Range
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('price') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('price') && (
                    <div className="mt-2 pl-6">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            placeholder="Min"
                            value={filters.priceRange.min}
                            onChange={(e) => updateFilter('priceRange', { 
                              ...filters.priceRange, 
                              min: Number(e.target.value) 
                            })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          />
                          <span className="text-gray-500">to</span>
                          <input
                            type="number"
                            placeholder="Max"
                            value={filters.priceRange.max}
                            onChange={(e) => updateFilter('priceRange', { 
                              ...filters.priceRange, 
                              max: Number(e.target.value) 
                            })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Brands */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('brands')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <TagIcon className="h-4 w-4 mr-2" />
                      Brands
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('brands') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('brands') && (
                    <div className="mt-2 space-y-1 pl-6">
                      {['Apple', 'Samsung', 'Microsoft', 'Dell', 'HP', 'Lenovo', 'Sony', 'LG'].map((brand) => (
                        <label key={brand} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.brands.includes(brand)}
                            onChange={() => handleBrandToggle(brand)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{brand}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Condition */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('condition')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <CheckIcon className="h-4 w-4 mr-2" />
                      Condition
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('condition') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('condition') && (
                    <div className="mt-2 space-y-1 pl-6">
                      {PRODUCT_CONDITIONS.map((condition) => (
                        <label key={condition} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.conditions.includes(condition)}
                            onChange={() => handleConditionToggle(condition)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{condition}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Product Type */}
                <div className="mb-4">
                  <button
                    onClick={() => toggleExpanded('productType')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center">
                      <ShoppingBagIcon className="h-4 w-4 mr-2" />
                      Product Type
                    </span>
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${
                        expandedItems.includes('productType') ? 'rotate-90' : ''
                      }`}
                    />
                  </button>
                  {expandedItems.includes('productType') && (
                    <div className="mt-2 space-y-1 pl-6">
                      {['Brand New', 'Refurbished', 'Used', 'Vintage'].map((productType) => (
                        <label key={productType} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={filters.productTypes.includes(productType)}
                            onChange={() => handleProductTypeToggle(productType)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-600">{productType}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Apply Filters Button */}
                <div className="pt-4">
                  <button
                    onClick={applyFilters}
                    className="w-full bg-primary-600 text-white text-sm font-medium py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center">
              RecyclerBin v1.0.0
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
